import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Stack } from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import BASE_URL from '../../main/urls/urls';

const severityOptions = ['Mild', 'Moderate', 'Severe'];

const CreateDiagnosis = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    patientId: '',
    doctorId: '',
    diagnosisDate: '',
    condition: '',
    severity: 'Mild',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);

  useEffect(() => {
    // Fetch patients and doctors from backend
    axios.get(`${BASE_URL}/patients`)
      .then(res => setPatients(res.data))
      .catch(() => setPatients([]));
    axios.get(`${BASE_URL}/doctors`)
      .then(res => setDoctors(res.data))
      .catch(() => setDoctors([]));
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    const now = new Date().toISOString();
    const data = {
      ...form,
      createdAt: now,
      updatedAt: now
    };
    try {
      await axios.post(`${BASE_URL}/diagnoses`, data);
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('diagnosis.title')}</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit} autoComplete="off">
          <TextField
            select
            label={t('diagnosis.patient')}
            name="patientId"
            value={form.patientId}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
          >
            {patients.map(p => (
              <MenuItem key={p.id} value={p.id}>
                {p.fullName || p.name || p.id}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('diagnosis.doctor')}
            name="doctorId"
            value={form.doctorId}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
          >
            {doctors.map(d => (
              <MenuItem key={d.id} value={d.id}>
                {d.fullName || d.name || d.id}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('diagnosis.date')}
            name="diagnosisDate"
            type="datetime-local"
            value={form.diagnosisDate}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label={t('diagnosis.condition')}
            name="condition"
            value={form.condition}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            select
            label={t('diagnosis.severity')}
            name="severity"
            value={form.severity}
            onChange={handleChange}
            fullWidth
            margin="normal"
          >
            {severityOptions.map(opt => (
              <MenuItem key={opt} value={opt}>{t(`diagnosis.severity_${opt.toLowerCase()}`)}</MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('diagnosis.notes')}
            name="notes"
            value={form.notes}
            onChange={handleChange}
            fullWidth
            margin="normal"
            multiline
            rows={2}
          />
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button type="submit" variant="contained" color="success" disabled={loading}>
              {loading ? t('diagnosis.saving') : t('diagnosis.create')}
            </Button>
            <Button variant="outlined" onClick={onClose} disabled={loading}>
              {t('diagnosis.cancel')}
            </Button>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDiagnosis;